import { FormEvent, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel, Stat } from "../components/Panel";
import {
  validateProvisionWorkspaceInput,
  type ProvisionDepartmentInput
} from "../lib/adminProvisioning";
import {
  provisionWorkspace,
  type ProvisionWorkspaceResult
} from "../lib/api/adminProvisioningClient";
import {
  PACKAGE_LIMITS,
  calculateWorkspacePrice,
  formatPackageName,
  getPackageEndDate,
  getPackageLimit
} from "../lib/packageLimits";
import type { FocusType, PackageType, WorkspaceStatus } from "../types";

interface DepartmentDraft extends ProvisionDepartmentInput {
  id: string;
}

function createDepartmentDraft(index: number): DepartmentDraft {
  return {
    id: `department-${Date.now()}-${index}`,
    name: "",
    contributorEmail: ""
  };
}

export function AdminWorkspaceNew() {
  const today = new Date().toISOString().slice(0, 10);
  const [packageType, setPackageType] = useState<PackageType>("guided_digital_access_3m");
  const [status, setStatus] = useState<WorkspaceStatus>("draft");
  const [focusType, setFocusType] = useState<FocusType>("both");
  const [startDate, setStartDate] = useState(today);
  const [maxDepartments, setMaxDepartments] = useState(2);
  const [departments, setDepartments] = useState<DepartmentDraft[]>([
    createDepartmentDraft(1),
    createDepartmentDraft(2)
  ]);
  const [sendSetupEmails, setSendSetupEmails] = useState(false);
  const [result, setResult] = useState<ProvisionWorkspaceResult | undefined>();
  const [message, setMessage] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const endDate = useMemo(() => getPackageEndDate(startDate, packageType), [packageType, startDate]);
  const price = calculateWorkspacePrice(packageType, Math.max(1, departments.filter((department) => department.name?.trim()).length));
  const packageLimit = getPackageLimit(packageType);

  function updateDepartment(id: string, field: keyof ProvisionDepartmentInput, value: string) {
    setDepartments((current) =>
      current.map((department) => (department.id === id ? { ...department, [field]: value } : department))
    );
  }

  function addDepartment() {
    setDepartments((current) => {
      if (current.length >= maxDepartments) return current;
      return [...current, createDepartmentDraft(current.length + 1)];
    });
  }

  function removeDepartment(id: string) {
    setDepartments((current) => current.length <= 1 ? current : current.filter((department) => department.id !== id));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setResult(undefined);
    setMessage(undefined);
    setError(undefined);

    const validated = validateProvisionWorkspaceInput({
      organizationName: String(formData.get("organizationName") ?? ""),
      organizationIndustry: String(formData.get("organizationIndustry") ?? ""),
      organizationCompanySize: String(formData.get("organizationCompanySize") ?? ""),
      workspaceName: String(formData.get("workspaceName") ?? ""),
      packageType,
      startDate,
      endDate,
      status,
      goal: String(formData.get("goal") ?? ""),
      focusType,
      maxDepartments,
      ownerEmail: String(formData.get("ownerEmail") ?? ""),
      ownerName: String(formData.get("ownerName") ?? ""),
      departments,
      sendSetupEmails
    });

    if (!validated.ok) {
      setError(validated.message);
      return;
    }

    setIsSubmitting(true);
    try {
      const provisioned = await provisionWorkspace(validated.value);
      setResult(provisioned);
      setMessage("Workspace wurde in D1 provisioniert. Setup-Links können kopiert werden.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Workspace konnte nicht provisioniert werden.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function copySetupLink(link: string) {
    await navigator.clipboard.writeText(link);
    setMessage("Setup-Link wurde kopiert.");
  }

  return (
    <Layout
      eyebrow="Workspace Provisioning"
      title="Workspace anlegen"
      description="HAUFFE Admins provisionieren echte Organizations, Workspaces, Owner, Abteilungen und optionale Contributor in D1."
    >
      {message ? (
        <Panel title="Status" variant="soft">
          <p>{message}</p>
        </Panel>
      ) : null}
      {error ? (
        <Panel title="Hinweis" variant="soft">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}

      <Panel title="Neuer Customer Value Workspace" variant="accent">
        <form className="form form-grid" onSubmit={handleSubmit}>
          <h3 className="form-section-title">Organization</h3>
          <label>
            Name
            <input name="organizationName" placeholder="Unternehmensname" required />
          </label>
          <label>
            Industry
            <input name="organizationIndustry" placeholder="Branche optional" />
          </label>
          <label>
            Company Size
            <input name="organizationCompanySize" placeholder="z. B. 51-200" />
          </label>

          <h3 className="form-section-title">Workspace</h3>
          <label>
            Workspace Name
            <input name="workspaceName" placeholder="Customer Value Workspace" required />
          </label>
          <label>
            Paket
            <select
              name="packageType"
              value={packageType}
              onChange={(event) => {
                const nextPackage = event.target.value as PackageType;
                const nextLimit = getPackageLimit(nextPackage).maxDepartments;
                setPackageType(nextPackage);
                setMaxDepartments((current) => Math.min(current, nextLimit));
                setDepartments((current) => current.slice(0, nextLimit));
              }}
            >
              {(Object.keys(PACKAGE_LIMITS) as PackageType[]).map((packageType) => (
                <option key={packageType} value={packageType}>
                  {formatPackageName(packageType)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value as WorkspaceStatus)}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="locked">Locked</option>
            </select>
          </label>
          <label>
            Fokus
            <select value={focusType} onChange={(event) => setFocusType(event.target.value as FocusType)}>
              <option value="both">Existing + New Customers</option>
              <option value="existing_customers">Existing Customers</option>
              <option value="new_customers">New Customers</option>
            </select>
          </label>
          <label>
            Startdatum
            <input
              name="startDate"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label>
            Enddatum
            <input name="endDate" type="date" value={endDate} readOnly />
          </label>
          <label>
            Max Departments
            <input
              name="maxDepartments"
              type="number"
              min="1"
              max={packageLimit.maxDepartments}
              value={maxDepartments}
              onChange={(event) => {
                const nextValue = Math.max(1, Math.min(packageLimit.maxDepartments, Number(event.target.value)));
                setMaxDepartments(nextValue);
                setDepartments((current) => current.slice(0, nextValue));
              }}
            />
          </label>
          <label className="form-wide">
            Goal
            <textarea name="goal" rows={3} placeholder="Ziel des Workspace optional" />
          </label>

          <h3 className="form-section-title">Company Owner</h3>
          <label>
            E-Mail
            <input name="ownerEmail" type="email" placeholder="owner@unternehmen.de" required />
          </label>
          <label>
            Name
            <input name="ownerName" placeholder="Name optional" />
          </label>

          <h3 className="form-section-title">Departments</h3>
          <div className="form-wide provisioning-departments">
            {departments.map((department, index) => (
              <div className="provisioning-department" key={department.id}>
                <label>
                  Department Name
                  <input
                    value={department.name ?? ""}
                    onChange={(event) => updateDepartment(department.id, "name", event.target.value)}
                    placeholder={`Abteilung ${index + 1}`}
                    required
                  />
                </label>
                <label>
                  Contributor E-Mail optional
                  <input
                    type="email"
                    value={department.contributorEmail ?? ""}
                    onChange={(event) => updateDepartment(department.id, "contributorEmail", event.target.value)}
                    placeholder="contributor@unternehmen.de"
                  />
                </label>
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={() => removeDepartment(department.id)}
                  disabled={departments.length <= 1}
                >
                  Entfernen
                </button>
              </div>
            ))}
            <button
              className="button button-secondary"
              type="button"
              onClick={addDepartment}
              disabled={departments.length >= maxDepartments}
            >
              Department hinzufügen
            </button>
          </div>

          <label className="checkbox-line form-wide">
            <input
              type="checkbox"
              checked={sendSetupEmails}
              onChange={(event) => setSendSetupEmails(event.target.checked)}
            />
            Setup-Links direkt per E-Mail senden
          </label>

          <div className="form-wide grid grid-3">
            <Stat label="Paketwert" value={`${price.toLocaleString("de-DE")} EUR netto`} />
            <Stat label="Enthaltene Abteilungen" value={String(packageLimit.includedDepartments)} />
            <Stat label="Maximal im Paket" value={String(packageLimit.maxDepartments)} />
          </div>

          <button className="button button-primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Provisioning läuft..." : "Workspace in D1 provisionieren"}
          </button>
        </form>
      </Panel>

      {result ? (
        <Panel title="Provisioning Ergebnis" variant="accent">
          <div className="grid grid-3">
            <Stat label="Organization" value={result.organization.name} />
            <Stat label="Workspace" value={result.workspace.name} />
            <Stat label="Departments" value={String(result.departments.length)} />
          </div>
          <div className="setup-link-list">
            {result.setupLinks.map((setupLink) => (
              <article className="setup-link-box" key={`${setupLink.userId}-${setupLink.role}`}>
                <strong>{setupLink.role === "owner" ? "Company Owner" : "Contributor"} · {setupLink.email}</strong>
                <span>Gültig bis {new Date(setupLink.expiresAt).toLocaleString("de-DE")}</span>
                <code>{setupLink.setupLink}</code>
                <button className="button button-secondary" type="button" onClick={() => copySetupLink(setupLink.setupLink)}>
                  Link kopieren
                </button>
                {setupLink.emailSent ? <p className="form-note">E-Mail wurde versendet.</p> : null}
                {setupLink.emailError ? <p className="form-note">{setupLink.emailError}</p> : null}
              </article>
            ))}
          </div>
          <div className="hero-actions">
            <a className="button button-primary" href={`/admin/workspaces/${result.workspace.id}`}>
              Workspace öffnen
            </a>
            <a className="button button-secondary" href="/admin">
              Zur Admin-Übersicht
            </a>
          </div>
        </Panel>
      ) : null}
    </Layout>
  );
}
